import { toEntries } from "@/helpers/utils.helper.ts";
import { ScoreTag } from "@/pages/talent/performance-reviews/view/components/ScoreTag.tsx";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiPerformanceReviewParticipantSectionsReport,
  ApiPerformanceReviewParticipantSectionsReportDataSection,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { Box, Button, Stack, Table, tableTheme, Title } from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { useNavigate } from "react-router-dom";
import { ApiFormParticipantStatus } from "@/types/forms/form.types.ts";

export const PerformanceReviewParticipantSectionsTable = ({
  sectionsReport,
  editReviewerId,
}: {
  sectionsReport: ApiPerformanceReviewParticipantSectionsReport;
  editReviewerId?: number;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columnHelper =
    createColumnHelper<ApiPerformanceReviewParticipantSectionsReportDataSection>();
  const columns = useMemo<
    ColumnDef<ApiPerformanceReviewParticipantSectionsReportDataSection>[]
  >(() => {
    const inner: DisplayColumnDef<ApiPerformanceReviewParticipantSectionsReportDataSection>[] = [];

    inner.push(
      columnHelper.display({
        id: "section",
        header: t("common:section"),
        cell: (cell) =>
          cell.row.original.title ? (
            <Title header={cell.row.original.title} size={"sm"} />
          ) : (
            <Title header={t("common:untitled")} className={"text-secondary"} size={"sm"} />
          ),
        ...ColumnSizes.standard,
        footer: () => {
          return (
            <Box
              className={twMerge(
                tableTheme.table.tbody.td,
                "py-3.5 border-t-1 border-t-brand-dark"
              )}
            >
              <Title header={t("common:total")} size={"sm"} />
            </Box>
          );
        },
      }),
      columnHelper.display({
        id: "all",
        header: t("common:all"),
        cell: (cell) => {
          const avgScaleScore = cell.row.original.overall_scale_score.avg_scale_score;
          return (
            <Stack direction="horizontal">
              <ScoreTag score={avgScaleScore} />
            </Stack>
          );
        },
        ...ColumnSizes.narrow,
        footer: () => {
          const avgScaleScore = sectionsReport.report.total.overall_scale_score.avg_scale_score;

          return (
            <Box
              className={twMerge(tableTheme.table.tbody.td, "py-3 border-t-1 border-t-brand-dark")}
            >
              <Stack direction="horizontal" className={"px-2"}>
                <ScoreTag score={avgScaleScore} />
              </Stack>
            </Box>
          );
        },
      })
    );

    for (const [index, reviewer_type] of toEntries(sectionsReport.config.reviewer_types)) {
      inner.push(
        columnHelper.display({
          id: `reviewer-type-${reviewer_type.id}`,
          header:
            PerformanceReviewReviewerTypeItems().find((item) => item.id == reviewer_type.type)
              ?.shortTitle ??
            reviewer_type.title ??
            t("common:no_title"),
          cell: (cell) => {
            const avgScaleScore =
              cell.row.original.reviewer_type_scale_scores[index]?.avg_scale_score;
            return (
              <Stack direction={"horizontal"}>
                <ScoreTag score={avgScaleScore} variant={"secondary"} />
              </Stack>
            );
          },
          ...ColumnSizes.narrow,

          footer: () => {
            const avgScaleScore =
              sectionsReport.report.total.reviewer_type_scale_scores[index]?.avg_scale_score;
            const isModified = sectionsReport.modified_reviewer_type_ids.includes(reviewer_type.id);
            return (
              <Box
                className={twMerge(
                  tableTheme.table.tbody.td,
                  "py-3 border-t-1 border-t-brand-dark"
                )}
              >
                <Stack direction="horizontal" items="center" gap="xs" className={"px-2"}>
                  <ScoreTag score={avgScaleScore} variant={"secondary"} isModified={isModified} />
                </Stack>
              </Box>
            );
          },
        })
      );
    }

    return inner;
  }, [sectionsReport]);

  return (
    <Stack gap={"md"}>
      <Stack className="justify-between pt-8" direction="horizontal" items="center">
        <Title header={t("common:results")} size={"md"} />
        {editReviewerId && (
          <Button
            variant={"secondary"}
            size={"md"}
            onClick={async () => {
              navigate(`/performance-reviews/reviewers/${editReviewerId}/respond`, {
                state: {
                  forcedReviewerStatus: ApiFormParticipantStatus.in_progress,
                  from: location.pathname,
                },
              });
            }}
          >
            {t("scorecards:change_answers")}
          </Button>
        )}
      </Stack>
      <Table data={sectionsReport.report.sections} columns={columns} stickyFirstColumn={true} />
    </Stack>
  );
};
